// ── TermsForm ──
import { useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { Badge, Btn, Card, FInput, FInputNum, FSelect, Field, Icon, Modal, ReadOnlyForm, Spinner, TabBtn, Td, TdNum, Th } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { NotesPanel } from "../components/NotesPanel";
import { useApp } from "../../../shared/appContext";

// Terms percent columns are stored as DECIMALS (0.08875 = 8.875%) — verified
// against the table — but every one of them is labelled "(%)" on screen. The
// form used to bind the decimal straight to the input, so an 8.875% margin
// displayed as "0.08875", and the blank-record defaults ("5.5") were in the
// other unit entirely: a term added from this form stored 5.5, i.e. 550%.
// Everything below keeps the DB in decimals and the screen in percent.
const PCT_FIELDS = ["margin", "floorrate", "originationfeepercentage",
                    "amendmentfeepercentage", "annualfeepercentage", "unusedfeepercentage"];

// toFixed(6) because 0.08875 * 100 is 8.875000000000002 in binary floating
// point, and that is not a number anyone wants to see in a rate field.
const toPctText = v =>
  (v === null || v === undefined || v === "") ? "" : String(+(Number(v) * 100).toFixed(6));

export const TermsForm = ({ initial, loanid, wide, readOnly }) => {
    const { api, role, view, modal, setModal, closeModal, setConfirm, saving, error, saveRec, delRec } = useApp();
    const blank = {
      loanid, accrualmethod:"Actual/360", facility_type:"", term_type:"", ratetype:"Floating", rateindex:"SOFR",
      margin:"0.055", floorrate:"0.03", floorratetype:"SOFR", amortizationmonths:"36", termtomaturity:"24",
      paymentfrequency:"Monthly", maturitydate:"", expectedfundeddate:"", fundingdate:"",
      commitment:"", newcommitmentamount:"", fundingamount:"",
      originationfeetype:"Commitment Amt", originationfeepercentage:"0.015",
      amendmentfeepercentage:"", annualfeetype:"", annualfeepercentage:"",
      monthlyfeetype:"Collateral Monitoring", monthlyfee:"3000",
      unusedfeepercentage:"",
      bdo:"", underwriter:"", fundedby:"", floatdays:"2",
      achpayment:false, terminated:false, collateraltype:"", samedayinterestexemption:0, ratereset:"Monthly",
      drawperiodend:""

    };
    const [f, set, setF] = useForm(initial || blank);

    // The typed text is held locally rather than derived from f on every
    // render: going text -> decimal -> text would rewrite the box mid-keystroke
    // (typing "8." becomes "8"), so the user could never type a decimal point.
    const [pctText, setPctText] = useState(() =>
      Object.fromEntries(PCT_FIELDS.map(k => [k, toPctText((initial || blank)[k])])));
    const setPct = k => t => {
      setPctText(prev => ({ ...prev, [k]: t }));
      set(k)(t === "" ? null : Number(t) / 100);
    };
    const canFund = !!role.istermfunding;

    // Fixed vs floating. Switching to Fixed clears the index and floor so the
    // record matches what the statements will charge (Margin = the fixed rate).
    const isFixed = String(f.ratetype || "").trim().toLowerCase() === "fixed";
    const setRateType = v => {
      const val = (v && typeof v === "object" && v.target) ? v.target.value : v;
      // One functional update so the four fields can't clobber each other.
      if (String(val || "").toLowerCase() === "fixed") {
        setF(prev => ({ ...prev, ratetype: val, rateindex: "N/A", floorratetype: "N/A", floorrate: null }));
        setPctText(prev => ({ ...prev, floorrate: "" }));
      } else {
        setF(prev => ({ ...prev, ratetype: val }));
      }
    };

    // Re-read this term from the DB whenever the form opens for an existing term. The loan
    // page's cached `terms` list can be stale right after a save, and opening the modal does
    // not otherwise refetch — so pull the current row and merge it in. Guarantees the form is
    // never stale, independent of the list's state.
    useEffect(() => {
      const id = initial?.id;
      const lid = initial?.loanid || loanid;
      if (!id || !lid) return;
      api.get("getTerms?loanid=" + lid)
        .then(rows => {
          const fresh = (rows || []).find(r => String(r.id) === String(id));
          if (fresh) setF(prev => ({ ...prev, ...fresh }));
        })
        .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [bdoOptions, setBdoOptions] = useState([]);
    const [underwriterOptions, setUnderwriterOptions] = useState([]);
    const [funderOptions, setFunderOptions] = useState([]);
    // id -> FullName for ALL active users, so a person who was assigned on an old
    // term but has since lost the relevant flag (e.g. no longer a term-funder) can
    // still be shown by name in the dropdown instead of vanishing.
    const [userNames, setUserNames] = useState({});
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [termTab, setTermTab] = useState("details");
    const [checklist, setChecklist] = useState([]);
    const [clLoading, setClLoading] = useState(false);
    const [catalog, setCatalog] = useState([]);
    const [addingItem, setAddingItem] = useState("");

    const loadChecklist = useCallback(() => {
      if (!initial?.id) return;
      setClLoading(true);
      api.get("getClosingChecklist?termid="+initial.id)
        .then(d=>{ setChecklist(d.items||[]); setClLoading(false); })
        .catch(()=>{ setChecklist([]); setClLoading(false); });
    }, [initial?.id]);
    useEffect(()=>{ if(termTab==="checklist") loadChecklist(); }, [termTab, loadChecklist]);

    useEffect(()=>{
      if(termTab==="checklist") {
        api.get("getChecklistCatalog").then(setCatalog).catch(()=>setCatalog([]));
      }
    }, [termTab]);

    const generate = async () => {
      try {
        await api.post("generateClosingChecklist", { termid: initial.id });
        loadChecklist();
      } catch(e) {
        alert(e.message || "Could not generate checklist.");
      }
    };

    const addItem = async (catalogId) => {
      if(!catalogId) return;
      try {
        await api.post("addChecklistItem", { termid: initial.id, checklistcatalogid: catalogId });
        setAddingItem("");
        loadChecklist();
      } catch(e) {
        alert(e.message || "Could not add item.");
      }
    };

    useEffect(()=>{
      const opt = users => [{value:"",label:"— Select —"}, ...users.map(u=>({value:u.id,label:u.FullName}))];
      Promise.all([
        api.get("getUsers?filter=isbdo"),
        api.get("getUsers?filter=isunderwriter"),
        api.get("getUsers?filter=istermfunding"),
        // includeInactive: the three lists above are what you may PICK, and a
        // departed employee should not be pickable. This fourth call only puts a
        // name to an id already saved on the record, so it has to see everybody
        // — otherwise a term funded years ago shows "U004" forever.
        api.get("getUsers?includeInactive=1"),
      ]).then(([b,u,f,all])=>{
        setBdoOptions(opt(b)); setUnderwriterOptions(opt(u)); setFunderOptions(opt(f));
        const map = {};
        (all||[]).forEach(x=>{ map[String(x.id)] = { name: x.FullName, active: x.isactive !== 0 && x.isactive !== false }; });
        setUserNames(map);
        setUsersLoaded(true);
      })
      .catch(()=>{ setUsersLoaded(true); });
    },[]);

    // Ensure the currently-saved assignee is always an option, even if they no longer
    // carry the flag that populates this dropdown (or are inactive). Non-destructive:
    // the value stays selectable/visible instead of blanking out on open.
    const withCurrent = (options, val) => {
      if (val === undefined || val === null || val === "") return options;
      if (options.some(o => String(o.value) === String(val))) return options;
      // Prefer the resolved name. Until the user list has loaded, show a blank
      // placeholder rather than the raw id/email — avoids the id flashing then
      // swapping to the name. After load, fall back to the id if still unknown.
      // NB: label must be a non-empty string — FSelect renders {o.label || o}, so an
      // empty label would fall through to rendering the option object (React crash).
      // " " (nbsp) shows blank while the name loads without tripping that.
      // Mark a departed employee rather than listing them as if they were staff
      // — same wording DrawForm already uses for the equivalent case.
      const hit = userNames[String(val)];
      const label = hit
        ? (hit.active ? hit.name : hit.name + " (inactive)")
        : (usersLoaded ? String(val) : " ");
      return [...options, { value: val, label }];
    };
    const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;

    return (
        <Modal title={readOnly?"View Term":initial?"Edit Term":"Add Term"} onClose={closeModal} wide={wide}
        onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"terms-edit",rec:initial,onDone:modal.onDone}); }:null}>

        {/* ── Tab strip (only for saved terms) ── */}
        {initial?.id && (
          <div style={{ display:"flex", gap:3, borderBottom:"3px solid "+B.cyan, marginBottom:16 }}>
            {["details","checklist"].map(t=>(
              <TabBtn key={t} id={t} current={termTab}
                label={t==="details"?"Details":"Checklist"} onClick={setTermTab} />
            ))}
          </div>
        )}

        {/* ── DETAILS TAB ── */}
        {(!initial?.id || termTab==="details") && <>
        <ReadOnlyForm readOnly={readOnly}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 12px" }}>

          <ST title="Status" />
          <Field label="Pipeline Stage">
          <div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555" }}>{f.pipelinestage||"—"}</div></Field>
          <div /><div /><div />
          <ST title="Personnel" />
          <Field label="BDO"><FSelect value={f.bdo} onChange={set("bdo")} options={withCurrent(bdoOptions, f.bdo)} /></Field>
          <Field label="Underwriter"><FSelect value={f.underwriter} onChange={set("underwriter")} options={withCurrent(underwriterOptions, f.underwriter)} /></Field>
          <Field label="Expected Funded date"><FInput value={f.expectedfundeddate?.slice(0,10)||""} onChange={set("expectedfundeddate")} type="date" /></Field>
          <div />
          <ST title="Loan Structure" />

          <Field label="Collateral Type">
          <FSelect value={f.collateraltype||""} onChange={set("collateraltype")} options={["","ABL","CF"]} />
          </Field>
          {/* Delayed draw only. The last day of the draw period: payments up to
              the end of that month carry no principal, and amortization (funded
              since the balance was last zero / amortization months) starts the
              month after. Leave blank to fall back to the original schedule. */}
          {String(f.facility_type || "").trim() === "Delayed Draw Term Loan"
            ? <Field label="Draw Period End">
                <FInput value={f.drawperiodend?.slice(0,10)||""} onChange={set("drawperiodend")} type="date" />
              </Field>
            : <div />}
          <div /><div />
          <Field label="Facility Type"><FSelect value={f.facility_type} onChange={set("facility_type")} options={["","Revolving Line of Credit","Term Loan","Non Revolving Line of Credit","Delayed Draw Term Loan","Payment in Kind"]} /></Field>
          <Field label="Term Type"><FSelect value={f.term_type} onChange={set("term_type")} options={["","New Loan","Amendment with Funding","Amendment without Funding","Discretionary Amendment","Loan Renewal"]} /></Field>
          <Field label="Payment Frequency"><FSelect value={f.paymentfrequency} onChange={set("paymentfrequency")} options={["Monthly","Weekly"]} /></Field>
          <Field label="Amortization (mo)"><FInput value={f.amortizationmonths} onChange={set("amortizationmonths")} type="number" /></Field>
          <Field label="Term to Maturity (mo)"><FInput value={f.termtomaturity} onChange={set("termtomaturity")} type="number" /></Field>
          <Field label="Float Days"><FInput value={f.floatdays} onChange={set("floatdays")} type="number" /></Field>
          <Field label="Accrual Method"><FSelect value={f.accrualmethod} onChange={set("accrualmethod")} options={["Actual/360","Actual/365","30/360"]} /></Field>
          <Field label="Maturity Date"><FInput value={f.maturitydate?.slice(0,10)||""} onChange={set("maturitydate")} type="date" /></Field>
          <ST title="Funding" />
          <Field label="Funded by"><FSelect value={f.fundedby} onChange={set("fundedby")} options={withCurrent(funderOptions, f.fundedby)} disabled={!canFund} /></Field>
          <Field label="Funding Date"><FInput value={f.fundingdate?.slice(0,10)||""} onChange={set("fundingdate")} type="date" disabled={!canFund} /></Field>
          {!canFund && <div style={{ gridColumn:"3/-1", alignSelf:"end", fontSize:11, color:"var(--color-text-secondary)", paddingBottom:8 }}>Funding fields are editable only by term-funding users.</div>}
          <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <input type="checkbox" id="term-samedayinterestexemption" checked={!!f.samedayinterestexemption}
              onChange={e=>set("samedayinterestexemption")(e.target.checked ? 1 : 0)} disabled={!canFund}
              style={{ width:16, height:16, cursor: canFund ? "pointer" : "default" }} />
            <label htmlFor="term-samedayinterestexemption" style={{ fontSize:13, cursor: canFund ? "pointer" : "default" }}>
              Same-day interest exemption <span style={{ fontSize:11, color:"var(--color-text-secondary)" }}>— when checked, no interest is charged on this term's funding amount for the day it funds (same rule as on a draw).</span>
            </label>
          </div>
          <ST title="Commitment & Funding" />
          <Field label="Commitment ($)"><FInputNum  value={f.commitment} onChange={set("commitment")} type="number" /></Field>
          <Field label="New Commitment ($)"><FInputNum  value={f.newcommitmentamount} onChange={set("newcommitmentamount")} type="number" /></Field>
          <Field label="Funding Amount ($)"><FInputNum  value={f.fundingamount} onChange={set("fundingamount")} type="number" /></Field>
          <div />
          <ST title="Rate" />
          {/* Fixed-rate terms: Margin holds the fixed rate (index = 0 everywhere
              downstream), so the field is relabelled and index/floor are locked to
              N/A. saveTerms enforces the same rule server-side. */}
          <Field label="Rate Type"><FSelect value={f.ratetype} onChange={setRateType} options={["Fixed","Floating"]} /></Field>
          <Field label="Rate Index"><FSelect value={isFixed ? "N/A" : f.rateindex} onChange={set("rateindex")} options={["SOFR","PRIME","BSBY1M","N/A"]} disabled={isFixed} /></Field>
          <Field label={isFixed ? "Fixed Rate (%)" : "Margin (%)"}><FInput value={pctText.margin} onChange={setPct("margin")} type="number" /></Field>
          {/* Monthly: the prior month's last rate applies to the whole month (default).
              Daily: each rate applies from its own date — e.g. a participation where
              the lead bank resets the day prime moves. */}
          <Field label="Rate Reset"><FSelect value={isFixed ? "Monthly" : (f.ratereset || "Monthly")} onChange={set("ratereset")}
            options={[{value:"Monthly",label:"Monthly (prior month-end)"},{value:"Daily",label:"Daily (effective date)"}]} disabled={isFixed} /></Field>
          <Field label="Floor Rate Type"><FSelect value={isFixed ? "N/A" : f.floorratetype} onChange={set("floorratetype")} options={["SOFR","PRIME","BSBY1M","N/A"]} disabled={isFixed} /></Field>
          <Field label="Floor Rate (%)"><FInput value={isFixed ? "" : pctText.floorrate} onChange={setPct("floorrate")} type="number" disabled={isFixed} /></Field>
          {isFixed
            ? <div style={{ gridColumn:"3/-1", alignSelf:"end", fontSize:11, color:"var(--color-text-secondary)", paddingBottom:8 }}>Fixed rate: enter the full rate above. No index or floor applies.</div>
            : <><div /><div /></>}
          <ST title="Fees" />
          <Field label="Origination Fee Type"><FSelect value={f.originationfeetype} onChange={set("originationfeetype")} options={["Commitment Amt","Funded Amt"]} /></Field>
          <Field label="Origination Fee (%)"><FInput value={pctText.originationfeepercentage} onChange={setPct("originationfeepercentage")} type="number" /></Field>
          <div /><div />
          <Field label="Amendment Fee (%)"><FInput value={pctText.amendmentfeepercentage} onChange={setPct("amendmentfeepercentage")} type="number" /></Field>
          <div /><div /><div />
          <Field label="Annual Fee Type"><FSelect value={f.annualfeetype} onChange={set("annualfeetype")} options={["","Commitment Amt","Funded Amt"]} /></Field>
          <Field label="Annual Fee (%)"><FInput value={pctText.annualfeepercentage} onChange={setPct("annualfeepercentage")} type="number" /></Field>
          <div /><div />
          <Field label="Monthly Fee Type"><FSelect value={f.monthlyfeetype} onChange={set("monthlyfeetype")} options={["Collateral Monitoring","Arranger Fee"]} /></Field>
          <Field label="Monthly Fee ($)"><FInputNum value={f.monthlyfee} onChange={set("monthlyfee")} type="number" /></Field>
          <div /><div />
          <Field label="Unused Fee (%)"><FInput value={pctText.unusedfeepercentage} onChange={setPct("unusedfeepercentage")} type="number" /></Field>
          <div /><div /><div />

          <ST title="Flags" />
          <div style={{ gridColumn:"1/-1", display:"flex", gap:28, marginBottom:8 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.achpayment} onChange={e=>set("achpayment")(e.target.checked)} /> ACH payment
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.terminated} onChange={e=>set("terminated")(e.target.checked)} /> Terminated
            </label>
          </div>
        </div>
        </ReadOnlyForm>
        {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8 }}>{error}</div>}
        {initial?.id && <NotesPanel scope="record" entitytype="Term" entityid={initial.id} accountid={initial.accountid} loanid={initial.loanid||loanid} source="Term" />}
        </>}

        {/* ── CHECKLIST TAB ── */}
        {initial?.id && termTab==="checklist" && (
          <div style={{ minHeight:200 }}>

            {/* Always-available add bar */}
            {role.canEdit && (
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:16, flexWrap:"wrap" }}>
                {checklist.length===0 && (
                  <>
                    <Btn variant="primary" onClick={generate}>Generate Checklist</Btn>
                    <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
                      from term type ({f.term_type||"—"})
                    </span>
                    <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>· or ·</span>
                  </>
                )}
                <FSelect
                  value={addingItem}
                  onChange={v=>{ const val = typeof v==="object"?v.target.value:v; setAddingItem(val); }}
                  options={["", ...catalog.filter(c=>!checklist.some(it=>String(it.checklistcatalogid)===String(c.id))).map(c=>c.name)]}
                  style={{ minWidth:240 }}
                />
                <Btn onClick={()=>{
                  const chosen = catalog.find(c=>c.name===addingItem);
                  if(chosen) addItem(chosen.id);
                }} disabled={!addingItem}>+ Add Item</Btn>
              </div>
            )}

            {clLoading ? <Spinner /> : checklist.length===0 ? (
              <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>
                No checklist items yet. Generate from the term type, or add items individually above.
              </div>
            ) : (
              <Card style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                  <thead><tr>
                    <Th>Order</Th><Th>Category</Th><Th>Item</Th><Th>Resp.</Th>
                    <Th>Doc Req'd</Th><Th>Completed</Th><Th>Document</Th><Th></Th>
                  </tr></thead>
                  <tbody>{checklist.map(it=>{
                    const done = !!it.completeddate;
                    const needsDoc = it.documentrequired && !it.unrequiredocument;
                    return (
                      <tr key={it.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"checklistitem-edit",rec:it,onDone:loadChecklist})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <TdNum>{it.order??"—"}</TdNum>
                        <Td>{it.checklistcategory||"—"}</Td>
                        <Td style={{ fontWeight:500 }}>{it.checklistitem||"—"}</Td>
                        <Td>{it.responsibleparty||"—"}</Td>
                        <Td>{it.unrequiredocument ? <span style={{ color:"var(--color-text-secondary)" }}>waived</span> : (it.documentrequired?"✓":"—")}</Td>
                        <Td>{done ? <Badge s="Complete" /> : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}</Td>
                        <Td onClick={e=>e.stopPropagation()}>
                          {it.boxURL ? <a href={it.boxURL} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Link</a>
                            : needsDoc ? <span style={{ color:B.amberDark, fontSize:11 }}>required</span> : "—"}
                        </Td>
                        <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                          <Btn onClick={()=>setModal({type:"checklistitem-view",rec:it})}><Icon type="view" /></Btn>
                          {role.canEdit&&<Btn onClick={()=>setModal({type:"checklistitem-edit",rec:it,onDone:loadChecklist})}><Icon type="edit" /></Btn>}
                          {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this checklist item?",onConfirm:()=>delRec("ClosingChecklistItems",it.id,"Deleted checklist item",loadChecklist)})} variant="danger"><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </Card>
            )}
          </div>
        )}

        {/* ── Footer (visible on every tab) ── */}
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
          <Btn onClick={closeModal}>{(readOnly||termTab==="checklist")?"Close":"Cancel"}</Btn>
          {!readOnly&&termTab!=="checklist"&&<Btn disabled={saving} onClick={()=>saveRec("saveTerms",{...f,loanid:f.loanid||loanid},initial?"Edited":"Added","Terms",(initial?"Updated":"Added")+" terms",modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
        </div>

      </Modal>
    );
  };
